import { PlusIcon, TrashIcon } from "@heroicons/react/outline";
import { observer } from "mobx-react-lite";
import { CheckCircleIcon } from "lucide-react";
import { useMemo } from "react";

import { normalizeConditionForOperator } from "./utils";

import { BlockProps } from "@/components/PowerBlock/Block";
import { withBlockStore, useBlock } from "@/components/PowerBlock/Block/block-store";
import { useIsReadonly } from "@/components/PowerBlock/Block/readonly-context";
import SourcesField from "@/components/PowerBlock/Block/SourcesField";
import SelectSource, { SourceType } from "@/components/PowerBlock/Block/SourcesField/SelectSource";
import SectionGroup from "@/components/PowerBlock/Block/SectionGroup";
import ValidatedField from "@/components/PowerBlock/Block/ValidatedField";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RadioGroup } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ConditionConfiguration } from "@/types/evaluate";
import {
  OperatorType,
  isUnaryOperator,
} from "@/components/Workflows/WorkflowCanvas/Canvas/ContextMenu/Conditions/constants";

const operatorOptions = [
  OperatorType.EQUAL,
  OperatorType.NOT_EQUAL,
  OperatorType.LESS_THAN,
  OperatorType.GREATER_THAN,
  OperatorType.LESS_THAN_EQUAL,
  OperatorType.GREATER_THAN_EQUAL,
  OperatorType.IN,
  OperatorType.NOT_IN,
  OperatorType.IS_NULL,
  OperatorType.IS_NOT_NULL,
];

const matchModeOptions = [
  {
    id: "condition-match-all",
    label: "All conditions must pass",
    value: "all",
  },
  {
    id: "condition-match-any",
    label: "Any condition can pass",
    value: "any",
  },
];

const defaultCondition = {
  position: 0,
  operator: OperatorType.EQUAL as const,
  left_config: { type: "source" as const, source: { name: "", json_path: null } },
  right_config: { type: "static_value" as const, static_value: "" },
};

const emptySources: Record<SourceType, string[]> = {
  [SourceType.Column]: [],
  [SourceType.Dataset]: [],
  [SourceType.InputVariable]: [],
  [SourceType.Dependency]: [],
};

interface ValueFieldProps {
  label: string;
  isSource: boolean;
  disabled: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

const ValueField = ({ label, isSource, disabled, onToggle, children }: ValueFieldProps) => (
  <ValidatedField name={label} path="">
    <div className="space-y-2.5">
      {children}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-auto justify-start px-0 text-xs font-medium text-blue-500 hover:bg-transparent hover:text-blue-700"
        disabled={disabled}
        onClick={onToggle}
      >
        {isSource ? "Switch to static value" : "Switch to source"}
      </Button>
    </div>
  </ValidatedField>
);

const ConditionBlockCore = observer(
  ({ sources = emptySources }: Omit<BlockProps<ConditionConfiguration>, "DefaultFields">) => {
    const block = useBlock<ConditionConfiguration>();
    const isReadonly = useIsReadonly();

    const configuration = block.configuration ?? {
      is_and: true,
      conditionals: [defaultCondition],
      output_on_fail: "stop_execution" as const,
    };

    const conditionals = useMemo(
      () => configuration.conditionals ?? [defaultCondition],
      [configuration.conditionals],
    );

    const updateConfiguration = (next: ConditionConfiguration) => {
      block.setConfiguration({
        ...next,
        output_on_fail: "stop_execution",
      });
    };

    const updateCondition = (index: number, partial: Record<string, unknown>) => {
      const next = [...conditionals];
      next[index] = { ...next[index], ...partial };
      updateConfiguration({
        ...configuration,
        conditionals: next.map((c, i) => ({ ...c, position: i })),
      });
    };

    const updateOperator = (
      index: number,
      operator: ConditionConfiguration["conditionals"][number]["operator"],
    ) => {
      const next = [...conditionals];
      next[index] = normalizeConditionForOperator(next[index], operator);
      updateConfiguration({
        ...configuration,
        conditionals: next.map((c, i) => ({ ...c, position: i })),
      });
    };

    return (
      <SectionGroup
        title="Conditions"
        description="Define left/right values and operators for each condition"
        icon={<CheckCircleIcon className="h-4 w-4" />}
      >
        <div className="space-y-5">
          {conditionals.length > 1 && (
            <RadioGroup
              value={configuration.is_and ? "all" : "any"}
              onValueChange={value =>
                updateConfiguration({ ...configuration, is_and: value === "all" })
              }
              disabled={isReadonly}
              items={matchModeOptions}
            />
          )}

          {conditionals.map((condition, index) => {
            const leftIsSource = "source" in condition.left_config;
            const rightIsSource = "source" in condition.right_config;
            const unary = isUnaryOperator(condition.operator);

            return (
              <div key={condition.position} className="space-y-2 pb-5 last:pb-0">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm font-medium text-gray-700">Condition {index + 1}</span>
                  {!isReadonly && conditionals.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-auto px-0 text-xs font-medium text-gray-500 hover:bg-transparent hover:text-gray-700"
                      onClick={() =>
                        updateConfiguration({
                          ...configuration,
                          conditionals: conditionals
                            .filter((_, i) => i !== index)
                            .map((c, i) => ({ ...c, position: i })),
                        })
                      }
                    >
                      <TrashIcon className="mr-1 h-3.5 w-3.5" /> Remove
                    </Button>
                  )}
                </div>

                <SourcesField name={null}>
                  <div className="space-y-4">
                    <ValueField
                      label="Left value"
                      isSource={leftIsSource}
                      disabled={isReadonly}
                      onToggle={() =>
                        updateCondition(index, {
                          left_config: leftIsSource
                            ? { type: "static_value", static_value: "" }
                            : { type: "source", source: { name: "", json_path: null } },
                        })
                      }
                    >
                      {leftIsSource ? (
                        (() => {
                          const sourceConfig = condition.left_config.source;
                          return (
                            <SelectSource
                              sources={sources}
                              value={sourceConfig?.name}
                              onValueChange={value =>
                                updateCondition(index, {
                                  left_config: {
                                    type: "source",
                                    source: {
                                      ...(sourceConfig || { json_path: null }),
                                      name: value || "",
                                    },
                                  },
                                })
                              }
                              disabled={isReadonly}
                              type={SourceType.Column}
                            />
                          );
                        })()
                      ) : (
                        <Input
                          value={
                            "static_value" in condition.left_config
                              ? condition.left_config.static_value || ""
                              : ""
                          }
                          onChange={e =>
                            updateCondition(index, {
                              left_config: { type: "static_value", static_value: e.target.value },
                            })
                          }
                          disabled={isReadonly}
                          placeholder="Enter static value"
                        />
                      )}
                    </ValueField>

                    <ValidatedField name="Operator" path="">
                      <Select
                        value={condition.operator}
                        onValueChange={value =>
                          updateOperator(index, value as typeof condition.operator)
                        }
                        disabled={isReadonly}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {operatorOptions.map(op => (
                            <SelectItem key={op} value={op}>
                              {op}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </ValidatedField>

                    {!unary && (
                      <ValueField
                        label="Right value"
                        isSource={rightIsSource}
                        disabled={isReadonly}
                        onToggle={() =>
                          updateCondition(index, {
                            right_config: rightIsSource
                              ? { type: "static_value", static_value: "" }
                              : { type: "source", source: { name: "", json_path: null } },
                          })
                        }
                      >
                        {rightIsSource ? (
                          (() => {
                            const sourceConfig =
                              "source" in condition.right_config
                                ? condition.right_config.source
                                : undefined;
                            return (
                              <SelectSource
                                sources={sources}
                                value={sourceConfig?.name}
                                onValueChange={value =>
                                  updateCondition(index, {
                                    right_config: {
                                      type: "source",
                                      source: {
                                        ...(sourceConfig || { json_path: null }),
                                        name: value || "",
                                      },
                                    },
                                  })
                                }
                                disabled={isReadonly}
                                type={SourceType.Column}
                              />
                            );
                          })()
                        ) : (
                          <Input
                            value={condition.right_config.static_value}
                            onChange={e =>
                              updateCondition(index, {
                                right_config: {
                                  type: "static_value",
                                  static_value: e.target.value,
                                },
                              })
                            }
                            placeholder={
                              condition.operator === "in" || condition.operator === "not_in"
                                ? "value1,value2,value3"
                                : "Enter static value"
                            }
                            disabled={isReadonly}
                          />
                        )}
                      </ValueField>
                    )}
                  </div>
                </SourcesField>
              </div>
            );
          })}

          {!isReadonly && (
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                updateConfiguration({
                  ...configuration,
                  conditionals: [
                    ...conditionals,
                    { ...defaultCondition, position: conditionals.length },
                  ],
                })
              }
            >
              <PlusIcon className="mr-1 h-4 w-4" /> Add condition
            </Button>
          )}
        </div>
      </SectionGroup>
    );
  },
);

export default withBlockStore<ConditionConfiguration>(ConditionBlockCore);
